import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  useTheme,
  InputAdornment,
  CircularProgress,
  Alert,
  Fade
} from '@mui/material';
import {
  Close as CloseIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import { gsap } from 'gsap';
import customerAuthService from '../../services/customerAuthService';

const CustomerAuthDialog = ({ open, onClose, websitePath, onLoginSuccess }) => {
  const theme = useTheme();
  const [mode, setMode] = useState('login'); // 'login' or 'signup'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [checkingPhone, setCheckingPhone] = useState(false);
  
  // Form states
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [signupData, setSignupData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });

  // Refs for animations
  const containerRef = useRef(null);
  const formRef = useRef(null);
  const titleRef = useRef(null);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      // Reset after close animation (timeout optional, keeping simple)
      setTimeout(() => {
        setMode('login');
        setError('');
        setLoginData({ email: '', password: '' });
        setSignupData({
          name: '',
          email: '',
          phone: '',
          password: '',
          confirmPassword: ''
        });
      }, 300);
    } else {
      // Animate entrance
      if (containerRef.current) {
        gsap.fromTo(containerRef.current,
          { opacity: 0, scale: 0.9, y: 20 },
          { opacity: 1, scale: 1, y: 0, duration: 0.5, ease: 'back.out(1.2)' }
        );
      }
    }
  }, [open]);

  // Animate mode switch
  useEffect(() => {
    if (formRef.current && open) {
      const tl = gsap.timeline();
      
      // Fade out
      tl.to(formRef.current, { 
        opacity: 0, 
        x: mode === 'login' ? 50 : -50, 
        duration: 0.2 
      })
      // Reset position immediately
      .set(formRef.current, { x: mode === 'login' ? -50 : 50 })
      // Fade in
      .to(formRef.current, { 
        opacity: 1, 
        x: 0, 
        duration: 0.4, 
        ease: 'power2.out' 
      });

      // Animate title
      if (titleRef.current) {
        gsap.fromTo(titleRef.current,
          { y: -10, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.3, delay: 0.1 }
        );
      }
    }
  }, [mode, open]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (mode === 'login') {
      setLoginData(prev => ({ ...prev, [name]: value }));
    } else {
      if (name === 'phone') {
        const digits = value.replace(/\D/g, '').slice(0, 15);
        setSignupData(prev => ({ ...prev, phone: digits }));
        if (phoneError) setPhoneError('');
      } else {
        setSignupData(prev => ({ ...prev, [name]: value }));
      }
    }
    // Clear error on input
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let response;
      if (mode === 'login') {
        response = await customerAuthService.login(websitePath, loginData);
      } else {
        // Validate passwords match
        if (signupData.password !== signupData.confirmPassword) {
          throw { message: "Passwords don't match" };
        }
        // Basic format validations
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const phoneRegex = /^[0-9]{1,15}$/;
        if (!emailRegex.test(signupData.email)) {
          throw { message: 'Please enter a valid email address' };
        }
        if (!phoneRegex.test(signupData.phone)) {
          throw { message: 'Enter a valid phone (up to 15 digits)' };
        }
        if (emailError || phoneError) {
          throw { message: 'Please resolve the highlighted field errors' };
        }
        response = await customerAuthService.register(websitePath, signupData);
      }

      // Success animation
      if (onLoginSuccess) {
        onLoginSuccess(response.customer);
      }
      onClose();
    } catch (err) {
      console.error('Auth error:', err);
      setError(err.message || 'Authentication failed. Please try again.');
      
      // Shake animation on error
      if (containerRef.current) {
        gsap.to(containerRef.current, {
          x: [-5, 5, -5, 5, 0],
          duration: 0.4,
          ease: 'power2.inOut'
        });
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleEmailBlur = () => {
    if (mode !== 'signup') return;
    const email = (signupData.email || '').trim();
    if (!email) {
      setEmailError('');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setEmailError(emailRegex.test(email) ? '' : 'Invalid email format');
  };
  
  const handlePhoneBlur = () => {
    if (mode !== 'signup') return;
    const phone = signupData.phone || '';
    if (!phone) {
      setPhoneError('');
      return;
    }
    const phoneRegex = /^[0-9]{1,15}$/;
    setPhoneError(phoneRegex.test(phone) ? '' : 'Only digits, max 15');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          background: 'transparent',
          boxShadow: 'none'
        }
      }}
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(5px)',
          backgroundColor: 'rgba(0, 0, 0, 0.5)'
        }
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 4,
          overflow: 'hidden',
          position: 'relative',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
        }}
      >
        {/* Header Gradient */}
        <Box
          sx={{
            height: 120,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            color: 'white'
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
            }}
          >
            <CloseIcon />
          </IconButton>
          
          <Typography
            ref={titleRef}
            variant="h4"
            fontWeight="bold"
            sx={{ 
              textShadow: '0 2px 10px rgba(0,0,0,0.2)',
              mt: 2 
            }}
          >
            {mode === 'login' ? 'Welcome Back' : 'Create Account'}
          </Typography>
        </Box>

        {/* Content */}
        <DialogContent sx={{ p: 4 }}>
          <Box ref={formRef} component="form" onSubmit={handleSubmit}>
            
            {error && (
              <Fade in>
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                  {error}
                </Alert>
              </Fade>
            )}

            {mode === 'login' ? (
              // LOGIN FORM
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={loginData.email}
                  onChange={handleInputChange}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={loginData.password}
                  onChange={handleInputChange}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Box>
            ) : (
              // SIGNUP FORM
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="name"
                  value={signupData.name}
                  onChange={handleInputChange}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={signupData.email}
                  onChange={handleInputChange}
                  onBlur={handleEmailBlur}
                  required
                  error={!!emailError}
                  helperText={emailError}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={signupData.phone}
                  onChange={handleInputChange}
                  onBlur={handlePhoneBlur}
                  required
                  error={!!phoneError}
                  helperText={phoneError}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={signupData.password}
                  onChange={handleInputChange}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <TextField
                  fullWidth
                  label="Confirm Password"
                  name="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={signupData.confirmPassword}
                  onChange={handleInputChange}
                  required
                  error={signupData.confirmPassword && signupData.password !== signupData.confirmPassword}
                  helperText={signupData.confirmPassword && signupData.password !== signupData.confirmPassword ? "Passwords don't match" : ""}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Box>
            )}

            <Button
              fullWidth
              variant="contained"
              size="large"
              type="submit"
              disabled={loading || checkingEmail || checkingPhone}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : (mode === 'login' ? <LoginIcon /> : <PersonAddIcon />)}
              sx={{ 
                mt: 4, 
                mb: 2,
                py: 1.5,
                borderRadius: 50,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                boxShadow: `0 8px 16px -4px ${theme.palette.primary.main}80`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 12px 20px -4px ${theme.palette.primary.main}90`,
                }
              }}
            >
              {loading ? 'Please Wait...' : (mode === 'login' ? 'Login' : 'Create Account')}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {mode === 'login' ? "Don't have an account?" : "Already have an account?"}
                <Button
                  onClick={() => setMode(mode === 'login' ? 'signup' : 'login')}
                  sx={{ 
                    ml: 1, 
                    fontWeight: 'bold',
                    textTransform: 'none',
                    '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }
                  }}
                >
                  {mode === 'login' ? 'Sign Up' : 'Log In'}
                </Button>
              </Typography>
            </Box>

          </Box>
        </DialogContent>
      </Box>
    </Dialog>
  );
};

export default CustomerAuthDialog;

